import React from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getMembersDB,
  getNoticesDB,
  getTransactionsDB,
  getGelanggangDB,
  getInvoicesDB,
  saveMemberDB,
  saveInvoiceDB,
  saveTransactionDB,
  getUjianBengkungRegistrationsDB,
  getActiveUjianBengkungEventDB,
  saveUjianBengkungRegistrationDB,
  getUjianBengkungFeesDB,
  getUjianBengkungFeeForLevel,
  UjianBengkungFeeConfig
} from '../../services/db';
import {
  Role,
  Gelanggang,
  UserProfile,
  getGelanggangSessions,
  GelanggangDetail,
  InvoiceItem,
  UjianBengkungRegistration,
  NoticeItem
} from '../../types';
import { createToyyibPayBill, verifyToyyibPayBillStatus, TOYYIBPAY_CONFIG } from '../../lib/toyyibpay';
import { Skeleton, CardSkeleton } from '../../components/common/Skeleton';
import { useToast } from '../../components/common/Toast';
import { Modal } from '../../components/common/Modal';
import {
  Users,
  CreditCard,
  Bell,
  Award,
  MapPin,
  ArrowUpRight,
  FileCheck2,
  ChevronRight,
  Flame,
  AlertCircle,
  DollarSign,
  Navigation,
  Edit2,
  Link as LinkIcon,
  Plus,
  Receipt,
  CheckCircle2,
  Clock,
  ExternalLink,
  RefreshCw,
  Wallet,
  FileText,
  Eye,
  Printer,
  ArrowLeft,
  Check,
  UserPlus,
  BookOpen,
  Sparkles,
  Send,
  Settings,
  Calendar,
  ShoppingBag,
  Package
} from 'lucide-react';

import { useMerchandiseData } from '../../hooks/useMerchandiseData';
import { cleanBengkungTitle, getSilatRankFromBengkung, getNextBengkungRank } from '../../data/bengkungRanks';
import { BengkungCulaBadge } from '../../components/common/BengkungCulaBadge';
import { getGoogleMapsUrl, formatPublicName, getGelanggangDisplayAddress, canUserAddChild, isSuperAdmin, isManagementOrTrainer, sanitizeMemberId, canEditGelanggangSessionAndPrice, isChildOfParent, isPaymentPendingAdminConfirmation, getOnboardingPaymentStatus, hasRolePermission } from '../../utils/formatters';
import { GelanggangManagementModal } from '../../components/common/GelanggangManagementModal';

interface DashboardPageProps {
  setActiveTab: (tab: string) => void;
}



export const DashboardPage: React.FC<DashboardPageProps> = ({ setActiveTab }) => {
  const { currentUser, selectedGelanggang, selectedChildId, setSelectedChildId } = useAuth();
  const { showToast } = useToast();
  const isExecutive = currentUser && (isSuperAdmin(currentUser) || hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari', 'Pemeriksa Kira-Kira']));
  const canManageGelanggang = currentUser && isManagementOrTrainer(currentUser);

  const isGelanggangOwner = (g: GelanggangDetail) => {
    return canEditGelanggangSessionAndPrice(currentUser, g);
  };

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [memberCount, setMemberCount] = React.useState<number>(0);
  const [pendingMemberCount, setPendingMemberCount] = React.useState<number>(0);
  const [noticeCount, setNoticeCount] = React.useState<number>(0);
  const [totalIncome, setTotalIncome] = React.useState<number>(0);
  const [unpaidBalance, setUnpaidBalance] = React.useState<number>(0);
  const [gelanggangs, setGelanggangs] = React.useState<GelanggangDetail[]>([]);
  const [familyMembers, setFamilyMembers] = React.useState<UserProfile[]>([]);
  const [members, setMembers] = React.useState<UserProfile[]>([]);
  const [invoices, setInvoices] = React.useState<InvoiceItem[]>([]);
  const [isGelanggangModalOpen, setIsGelanggangModalOpen] = React.useState<boolean>(false);
  const [selectedGelanggangToEdit, setSelectedGelanggangToEdit] = React.useState<GelanggangDetail | null>(null);

  // Ujian Bengkung Event & Pre-Registration Notification States
  const [ujianRegistrations, setUjianRegistrations] = React.useState<UjianBengkungRegistration[]>([]);
  const [activeUjianEvent, setActiveUjianEvent] = React.useState<NoticeItem | null>(null);
  const [isFinishRegModalOpen, setIsFinishRegModalOpen] = React.useState<boolean>(false);
  const [selectedCandidateToPay, setSelectedCandidateToPay] = React.useState<UjianBengkungRegistration | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = React.useState<boolean>(false);
  const [ujianFees, setUjianFees] = React.useState<UjianBengkungFeeConfig | null>(null);

  const { orders: merchandiseOrders } = useMerchandiseData();
  const myMerchandiseOrders = React.useMemo(() => {
    if (!currentUser) return [];
    return merchandiseOrders.filter(o => o.memberId === currentUser.id);
  }, [merchandiseOrders, currentUser]);

  const refreshGelanggangs = () => {
    getGelanggangDB().then((data) => setGelanggangs(data)).catch(() => { });
  };

  React.useEffect(() => {
    getUjianBengkungFeesDB().then(setUjianFees).catch(() => { });
    // Read from persistent cache first for instant sub-second render
    const cachedMembers = localStorage.getItem('asmm_db_members_v4');
    const cachedNotices = localStorage.getItem('asmm_db_notices_v4');
    const cachedTx = localStorage.getItem('asmm_db_transactions_v4');
    const cachedGel = localStorage.getItem('asmm_db_gelanggang_v5');
    const cachedInvoices = localStorage.getItem('asmm_db_invoices_v4');
    const cachedURegs = localStorage.getItem('asmm_ujian_bengkung_registrations_v1');

    if (cachedURegs) {
      try {
        setUjianRegistrations(JSON.parse(cachedURegs));
      } catch { }
    }

    if (cachedMembers && cachedNotices && cachedTx && cachedGel) {
      const parsedMembers = JSON.parse(cachedMembers);
      setMembers(parsedMembers);
      setMemberCount(parsedMembers.filter((m: any) => m.status === 'Aktif').length);
      const pending = parsedMembers.filter((m: any) => m.status === 'Menunggu Pengesahan');
      if (currentUser?.role === 'Ketua Gelanggang') {
        setPendingMemberCount(pending.filter((m: any) => m.gelanggang === currentUser.gelanggang).length);
      } else {
        setPendingMemberCount(pending.length);
      }
      setNoticeCount(JSON.parse(cachedNotices).length);
      const inc = JSON.parse(cachedTx).filter((t: any) => t.type === 'Income').reduce((sum: number, t: any) => sum + t.amount, 0);
      setTotalIncome(inc);
      setGelanggangs(JSON.parse(cachedGel));

      if (currentUser) {
        const myFam = parsedMembers.filter((m: any) => isChildOfParent(m, currentUser));
        setFamilyMembers(myFam);
        const childIds = myFam.map((c: any) => (c.id || '').toLowerCase());
        const curIdLower = (currentUser.id || '').toLowerCase();
        if (cachedInvoices) {
          const invs: InvoiceItem[] = JSON.parse(cachedInvoices);
          setInvoices(invs);
          const isPureWaris = (currentUser.role === 'Waris' || (currentUser.roles && currentUser.roles.includes('Waris'))) && !(currentUser as any).isAlsoPesilat;
          const myUnpaid = invs
            .filter(i => {
              if (isPureWaris && i.category === 'Yuran Bulanan' && i.memberId && i.memberId.toLowerCase() === curIdLower) {
                return false;
              }
              return ((i.memberId && i.memberId.toLowerCase() === curIdLower) || (i.memberId && childIds.includes(i.memberId.toLowerCase()))) && (i.status === 'Belum Dibayar' || i.status === 'Menunggu Pengesahan');
            })
            .reduce((s, i) => s + i.amount, 0);
          setUnpaidBalance(myUnpaid);
        }
      }
      setIsLoading(false);
    }

    const fetchFresh = () => {
      Promise.all([
        getMembersDB(),
        getNoticesDB(),
        getTransactionsDB(),
        getGelanggangDB(),
        getInvoicesDB(),
        getUjianBengkungRegistrationsDB(),
        getActiveUjianBengkungEventDB()
      ]).then(([freshMembers, notices, txs, gels, invs, uRegs, uEvt]) => {
        setMembers(freshMembers);
        setInvoices(invs);
        setMemberCount(freshMembers.filter(m => m.status === 'Aktif').length);
        const pending = freshMembers.filter(m => m.status === 'Menunggu Pengesahan');
        if (currentUser?.role === 'Ketua Gelanggang') {
          setPendingMemberCount(pending.filter(m => m.gelanggang === currentUser.gelanggang).length);
        } else {
          setPendingMemberCount(pending.length);
        }
        setNoticeCount(notices.length);
        const inc = txs.filter((t: any) => t.type === 'Income').reduce((sum: number, t: any) => sum + t.amount, 0);
        setTotalIncome(inc);
        setGelanggangs(gels);
        setUjianRegistrations(uRegs);
        setActiveUjianEvent(uEvt);

        if (currentUser) {
          const myFam = freshMembers.filter(m => isChildOfParent(m, currentUser));
          setFamilyMembers(myFam);
          const childIds = myFam.map(c => (c.id || '').toLowerCase());
          const curIdLower = (currentUser.id || '').toLowerCase();
          const isPureWaris = (currentUser.role === 'Waris' || (currentUser.roles && currentUser.roles.includes('Waris'))) && !(currentUser as any).isAlsoPesilat;
          const myUnpaid = invs
            .filter(i => {
              if (isPureWaris && i.category === 'Yuran Bulanan' && i.memberId && i.memberId.toLowerCase() === curIdLower) {
                return false;
              }
              return ((i.memberId && i.memberId.toLowerCase() === curIdLower) || (i.memberId && childIds.includes(i.memberId.toLowerCase()))) && (i.status === 'Belum Dibayar' || i.status === 'Menunggu Pengesahan');
            })
            .reduce((s, i) => s + i.amount, 0);
          setUnpaidBalance(myUnpaid);
        }
        setIsLoading(false);
      }).catch(() => {
        setIsLoading(false);
      });
    };

    fetchFresh();

    const handleDbUpdate = () => fetchFresh();
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleDbUpdate);
  }, [currentUser]);

  const myRelevantRegistrations = React.useMemo(() => {
    if (!currentUser) return [];
    const ids = [currentUser.id, ...familyMembers.map(f => f.id)];
    return ujianRegistrations.filter(r => ids.includes(r.memberId));
  }, [currentUser, familyMembers, ujianRegistrations]);

  const pendingFinishRegistrations = React.useMemo(() => {
    return myRelevantRegistrations.filter(r => r.status === 'pra-daftar' || r.status === 'menunggu-bayaran');
  }, [myRelevantRegistrations]);

  const isPendingPayment = React.useMemo(() => {
    return isPaymentPendingAdminConfirmation(currentUser, invoices, members);
  }, [currentUser, invoices, members]);

  const paymentStatus = React.useMemo(() => {
    return getOnboardingPaymentStatus(currentUser, invoices, members);
  }, [currentUser, invoices, members]);

  const handleCreateUjianInvoiceAndPay = async (candidate: UjianBengkungRegistration) => {
    if (!candidate) return;
    setIsProcessingPayment(true);
    try {
      const liveMember = members.find(m => m.id === candidate.memberId) || (currentUser?.id === candidate.memberId ? currentUser : null);
      const cleanCurrent = cleanBengkungTitle(liveMember?.bengkung || candidate.currentBengkung || 'Putih');
      const cleanTarget = cleanBengkungTitle(liveMember?.bengkung ? getNextBengkungRank(liveMember.bengkung) : (candidate.targetBengkung || 'Putih 1'));
      const feeAmount = getUjianBengkungFeeForLevel(cleanTarget, ujianFees || undefined);
      const invNo = `INV-UB-${Date.now().toString().slice(-6)}`;
      const newInvoice: InvoiceItem = {
        id: `inv-ub-${Date.now()}`,
        invoiceNo: invNo,
        memberId: candidate.memberId,
        memberName: candidate.memberName,
        memberPhone: candidate.memberPhone || currentUser?.phone || '',
        category: 'Yuran Bengkung',
        title: `Yuran Ujian Bengkung ${cleanTarget}`,
        description: `Pendaftaran rasmi & yuran pentaksiran Ujian Kenaikan Bengkung (${cleanCurrent} → ${cleanTarget}) - ${activeUjianEvent?.title || 'Ujian Bengkung ASMM'}`,
        amount: feeAmount,
        status: 'Belum Dibayar',
        dueDate: activeUjianEvent?.eventDate || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
        gelanggang: candidate.gelanggang,
        createdAt: new Date().toISOString(),
        items: [
          {
            id: `item-1`,
            description: `Yuran Pentaksiran Bengkung (${cleanTarget}) & Sijil ROS`,
            quantity: 1,
            unitPrice: feeAmount,
            amount: feeAmount
          }
        ]
      };

      await saveInvoiceDB(newInvoice);

      // Update registration status to menunggu-bayaran with invoice ID and event info
      const updatedReg: UjianBengkungRegistration = {
        ...candidate,
        currentBengkung: cleanCurrent,
        targetBengkung: cleanTarget,
        status: 'menunggu-bayaran',
        scheduledEventId: activeUjianEvent?.id,
        scheduledEventTitle: activeUjianEvent?.title,
        scheduledEventDate: activeUjianEvent?.eventDate,
        invoiceId: newInvoice.id
      };
      await saveUjianBengkungRegistrationDB(updatedReg);

      setIsFinishRegModalOpen(false);
      showToast(
        'success',
        'Invois Dicipta!',
        `Invois Ujian Bengkung berjaya dicipta untuk ${formatPublicName(candidate.memberName)}. Membuka halaman bayaran...`
      );

      // Navigate to payments page
      setActiveTab('payments');
      window.history.pushState(null, '', '/payments');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch {
      showToast('error', 'Ralat', 'Gagal memproses pendaftaran & invois.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Responsive Header Banner */}
      <div className="header-banner flex-col items-start justify-center gap-3 p-4 sm:p-6 h-auto">
        <div className="flex items-center gap-3 min-w-0 w-full">
          <img
            src={currentUser?.photoUrl || '/default-avatar.svg'}
            alt={currentUser?.name}
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl object-cover border border-slate-200 shadow-sm shrink-0"
            onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span className="text-[10px] font-extrabold text-asmm-red bg-red-50 px-2 py-0.5 rounded border border-red-100 uppercase shrink-0">
                {currentUser?.role}
              </span>
              {currentUser?.gelanggang && (
                <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 truncate">
                  {currentUser.gelanggang}
                </span>
              )}
            </div>
            <h2 className="text-lg sm:text-2xl font-extrabold text-slate-900 leading-tight">
              Selamat Datang, {currentUser?.name}
            </h2>
          </div>
        </div>

        <div className="w-full flex items-center justify-end gap-2 pt-1 flex-wrap">
          {currentUser && canUserAddChild(currentUser) && (
            <button
              onClick={() => setActiveTab('keluarga')}
              className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 font-bold text-xs rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
            >
              <Users className="w-4 h-4 text-asmm-red" />
              <span>Keluarga ({familyMembers.length})</span>
            </button>
          )}
          <button
            onClick={() => setActiveTab('payments')}
            className="px-4 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
          >
            <CreditCard className="w-4 h-4" />
            <span>Bayar Yuran</span>
          </button>
        </div>
      </div>

      {/* Pending Onboarding Payment / Admin Confirmation Alert Banner for Member */}
      {isPendingPayment && (
        <div className={`p-4 sm:p-5 rounded-3xl border shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-in ${paymentStatus.hasUploadedReceipt
            ? 'bg-blue-50/70 border-blue-200 text-blue-950'
            : 'bg-red-50/70 border-red-200 text-red-950'
          }`}>
          <div className="flex items-start sm:items-center gap-3.5 min-w-0">
            <div className={`p-3 rounded-2xl shrink-0 shadow-xs ${paymentStatus.hasUploadedReceipt
                ? 'bg-blue-600 text-white'
                : 'bg-asmm-red text-white animate-pulse'
              }`}>
              {paymentStatus.hasUploadedReceipt ? (
                <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
              ) : (
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
              )}
            </div>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${paymentStatus.hasUploadedReceipt
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-red-100 text-asmm-red'
                  }`}>
                  {paymentStatus.hasUploadedReceipt ? 'Menunggu Pengesahan Pentadbir' : 'Tindakan Diperlukan: Yuran Pendaftaran'}
                </span>
                <span className="text-[11px] font-bold text-slate-500">
                  Akses Terhad (Dashboard & Bayaran Sahaja)
                </span>
              </div>
              <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight">
                {paymentStatus.hasUploadedReceipt
                  ? 'Resit Pembayaran Sedang Disemak Oleh Pihak Pengurusan'
                  : `Sila Selesaikan Pembayaran Yuran Pendaftaran (RM${paymentStatus.totalAmount})`}
              </h3>
              <p className="text-xs text-slate-600 max-w-2xl leading-relaxed">
                {paymentStatus.hasUploadedReceipt
                  ? 'Resit pembayaran anda telah berjaya dimuat naik dan sedang disemak pentadbir gelanggang. Ciri-ciri penuh seperti Silibus, Keluarga, Notis dan Acara akan dibuka secara automatik sebaik sahaja pembayaran disahkan.'
                  : 'Pendaftaran anda telah disimpan. Ciri-ciri penuh persatuan (Silibus, Gelanggang, Tempahan) hanya akan diaktifkan secara automatik selepas pembayaran yuran pendaftaran disahkan oleh pentadbir.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start md:self-center shrink-0">
            <button
              type="button"
              onClick={() => {
                setActiveTab('payments');
                window.history.pushState(null, '', '/payments');
              }}
              className={`px-4 py-2.5 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer border-none active:scale-95 ${paymentStatus.hasUploadedReceipt
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-asmm-red hover:bg-asmm-crimson'
                }`}
            >
              <span>{paymentStatus.hasUploadedReceipt ? 'Semak Status Bayaran' : 'Bayar Sekarang'}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Pending Members Approval Alert Banner for Management */}
      {canManageGelanggang && pendingMemberCount > 0 && (
        <div
          onClick={() => {
            setActiveTab('members');
            window.history.pushState(null, '', '/members/baru/menunggu');
          }}
          className="bg-amber-50 border border-amber-200/90 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-amber-900 cursor-pointer hover:bg-amber-100/80 transition-all shadow-xs"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 bg-amber-500 text-white rounded-xl shrink-0 shadow-xs">
              <UserPlus className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm font-extrabold text-amber-950 flex items-center gap-2">
                <span>Kelulusan Ahli Baharu</span>
                <span className="text-[10px] font-black bg-amber-500 text-white px-2 py-0.5 rounded-full">
                  {pendingMemberCount} Permohonan
                </span>
              </div>
              <div className="text-[11px] text-amber-800 font-medium">
                Terdapat pesilat baharu mendaftar yang memerlukan semakan dan pengesahan anda.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-extrabold rounded-xl transition-all shadow-2xs self-start sm:self-center border-none cursor-pointer"
          >
            Semak & Luluskan →
          </button>
        </div>
      )}

      {/* UJIAN BENGKUNG SCHEDULED NOTIFICATION & PRE-REGISTRATION BANNER */}
      {activeUjianEvent && pendingFinishRegistrations.length > 0 && (
        <div className="bg-gradient-to-r from-amber-600 via-asmm-red to-red-700 text-white rounded-3xl p-5 sm:p-6 shadow-md border border-red-500/50 space-y-3.5 animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 shrink-0 text-amber-300 shadow-inner">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-[10px] font-black uppercase tracking-wider bg-amber-400 text-slate-950 px-2.5 py-0.5 rounded-full shadow-2xs">
                    Jadual Rasmi Diumumkan
                  </span>
                  <span className="text-xs font-mono text-white/90 font-bold flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {activeUjianEvent.eventDate} ({activeUjianEvent.eventTime || '9.00 Malam'})
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-extrabold text-white tracking-tight">
                  {activeUjianEvent.title}
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-center flex-wrap">
              {currentUser && (currentUser.role === 'Pengerusi' || currentUser.role === 'Timbalan Pengerusi' || currentUser.role === 'Ketua Gelanggang' || currentUser.role === 'Superadmin' || currentUser.role === 'AJK') && (
                <button
                  type="button"
                  onClick={() => {
                    window.history.pushState(null, '', '/grading');
                    setActiveTab('grading');
                  }}
                  className="text-xs bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-xl border border-red-500 text-white font-extrabold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
                  title="Buka portal penilaian & rubrik pemarkahan ujian bengkung mengikut silibus"
                >
                  <Award className="w-3.5 h-3.5 text-amber-300" />
                  <span>Penilaian Ujian Grading</span>
                </button>
              )}
              {currentUser && isSuperAdmin(currentUser) && (
                <button
                  type="button"
                  onClick={() => {
                    window.history.pushState(null, '', '/settings/yuran');
                    setActiveTab('settings');
                  }}
                  className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-xl border border-white/30 text-white font-extrabold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
                  title="Konfigurasi yuran ujian Bengkung mengikut tahap"
                >
                  <Settings className="w-3.5 h-3.5 text-amber-300" />
                  <span>Tetapan Yuran Bengkung</span>
                </button>
              )}
              <span className="text-xs bg-white/10 px-3 py-1.5 rounded-xl border border-white/20 text-slate-100 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-amber-300" />
                <span>{activeUjianEvent.eventLocation || 'Gelanggang Utama ASMM'}</span>
              </span>
            </div>
          </div>

          <div className="p-3.5 bg-black/20 backdrop-blur-sm rounded-2xl border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="text-xs font-medium text-white/90 leading-relaxed">
                Tahniah! Anda mempunyai <strong>{pendingFinishRegistrations.length} calon pra-daftar</strong> yang telah melengkapkan silibus dan layak untuk menduduki Ujian Bengkung.
              </p>
              <div className="flex flex-wrap gap-2 text-[11px] text-amber-200 font-semibold">
                {pendingFinishRegistrations.map((p) => {
                  const m = members.find(mem => mem.id === p.memberId) || (currentUser?.id === p.memberId ? currentUser : null);
                  const cur = cleanBengkungTitle(m?.bengkung || p.currentBengkung);
                  const tgt = cleanBengkungTitle(m?.bengkung ? getNextBengkungRank(m.bengkung) : p.targetBengkung);
                  return (
                    <span key={p.id} className="bg-white/10 px-2.5 py-0.5 rounded-lg border border-white/10 flex items-center gap-1">
                      <span>• {p.memberName}</span>
                      <span className="font-mono text-[9px] bg-white/20 px-1 rounded">{sanitizeMemberId(p.memberId)}</span>
                      <span>({cur} → {tgt})</span>
                    </span>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setSelectedCandidateToPay(pendingFinishRegistrations[0]);
                setIsFinishRegModalOpen(true);
              }}
              className="px-4 py-2.5 bg-white hover:bg-amber-50 text-slate-900 font-extrabold text-xs rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer active:scale-95"
            >
              <span>Selesaikan Pendaftaran</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      )}

      {!activeUjianEvent && pendingFinishRegistrations.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-emerald-950 shadow-xs animate-fadeIn">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-600 text-white rounded-2xl shrink-0 shadow-2xs">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="text-xs sm:text-sm font-extrabold text-emerald-900">
                  Pra-Pendaftaran Ujian Bengkung: Aktif
                </span>
                <span className="text-[10px] font-black bg-emerald-200/80 text-emerald-900 px-2 py-0.5 rounded-full">
                  {pendingFinishRegistrations.length} Calon Menunggu
                </span>
              </div>
              <p className="text-[11px] text-emerald-800 font-medium">
                Calon: <strong>{pendingFinishRegistrations.map(p => p.memberName).join(', ')}</strong>. Tarikh rasmi Ujian Bengkung tahunan belum dijadualkan oleh pihak pengurusan. Tiada bayaran diperlukan buat masa ini.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setActiveTab('syllabus')}
            className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-extrabold rounded-xl transition-all shadow-2xs self-start sm:self-center border-none cursor-pointer"
          >
            Lihat Silibus →
          </button>
        </div>
      )}



      {/* KPI Cards Grid (2-Cols on Mobile for Compact Modern View) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Members */}
        <div
          onClick={() => setActiveTab('members')}
          className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between cursor-pointer hover:border-slate-300 transition-all group"
        >
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider truncate group-hover:text-asmm-red transition-colors">
                Ahli Aktif
              </span>
              <Users className="w-4 h-4 text-asmm-red shrink-0" />
            </div>
            {isLoading ? (
              <Skeleton className="h-7 w-20 my-1" />
            ) : (
              <div className="text-lg sm:text-2xl font-extrabold text-slate-900">{memberCount} Pesilat</div>
            )}
          </div>
          <div className="flex items-center justify-between mt-1 pt-1 border-t border-slate-100/60">
            <p className="text-[10px] sm:text-[11px] text-slate-500 truncate group-hover:text-asmm-red transition-colors font-medium">Direktori Ahli →</p>
            {canManageGelanggang && pendingMemberCount > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('members');
                  window.history.pushState(null, '', '/members/baru/menunggu');
                }}
                className="text-[9px] font-black bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-full shadow-2xs cursor-pointer animate-pulse"
                title={`${pendingMemberCount} Permohonan Ahli Baru Menunggu Kelulusan`}
              >
                {pendingMemberCount} Baru
              </span>
            )}
          </div>
        </div>

        {/* Card 2: Kutipan Kewangan (Executive) OR Baki Tunggakan (Ahli Biasa) */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider truncate">
                {isExecutive ? 'Kutipan' : 'Tunggakan'}
              </span>
              {isExecutive ? (
                <CreditCard className="w-4 h-4 text-emerald-600 shrink-0" />
              ) : (
                <AlertCircle className={`w-4 h-4 shrink-0 ${unpaidBalance > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
              )}
            </div>
            {isLoading ? (
              <Skeleton className="h-7 w-24 my-1" />
            ) : isExecutive ? (
              <div className="text-lg sm:text-2xl font-extrabold text-emerald-700 font-mono truncate">RM {totalIncome.toFixed(2)}</div>
            ) : (
              <div className={`text-lg sm:text-2xl font-extrabold font-mono truncate ${unpaidBalance > 0 ? 'text-amber-600' : 'text-emerald-700'}`}>
                RM {unpaidBalance.toFixed(2)}
              </div>
            )}
          </div>
          <p className={`text-[10px] sm:text-[11px] font-medium mt-1 truncate ${isExecutive ? 'text-emerald-700' : unpaidBalance > 0 ? 'text-amber-600' : 'text-emerald-700'}`}>
            {isExecutive ? 'Verified' : unpaidBalance > 0 ? 'Belum Dibayar' : 'Tiada Tunggakan'}
          </p>
        </div>

        {/* Active Notices & RSVPs */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider truncate">Notis Aktif</span>
              <Bell className="w-4 h-4 text-amber-600 shrink-0" />
            </div>
            {isLoading ? (
              <Skeleton className="h-7 w-20 my-1" />
            ) : (
              <div className="text-lg sm:text-2xl font-extrabold text-amber-700">{noticeCount} Aktiviti</div>
            )}
          </div>
          <p className="text-[10px] sm:text-[11px] text-slate-500 mt-1 truncate">RSVP & Undian</p>
        </div>

        {/* Bengkung Rank Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between overflow-hidden">
          <div className="p-4 sm:p-5 pb-1 sm:pb-2 flex items-center justify-between">
            <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider truncate">Bengkung</span>
            <Award className="w-4 h-4 text-asmm-red shrink-0" />
          </div>

          {isLoading ? (
            <div className="px-4">
              <Skeleton className="h-8 w-full my-1 rounded-xl" />
            </div>
          ) : currentUser?.role === 'Waris' && !currentUser?.bengkung ? (
            <div className="px-5 py-3 text-[10px] text-slate-500 font-medium italic">
              Waris — tiada bengkung
            </div>
          ) : (
            <div className="w-full">
              <BengkungCulaBadge bengkung={currentUser?.bengkung || 'Putih 1'} size="sm" fullWidth={true} />
            </div>
          )}

          <div className="p-4 sm:p-5 pt-1 sm:pt-2">
            <p className="text-[10px] sm:text-[11px] text-asmm-red font-semibold truncate">
              {currentUser?.role === 'Waris' && !currentUser?.bengkung
                ? 'Tiada'
                : getSilatRankFromBengkung(currentUser?.bengkung || '')}
            </p>
          </div>
        </div>
      </div>

      {/* Keluarga & Tanggungan Section (For Ketua Gelanggang, Waris, and Authorized Management) */}
      {currentUser && canUserAddChild(currentUser) && (
        <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-50 text-asmm-red rounded-2xl border border-red-100/80 shadow-2xs shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-extrabold text-slate-900">Keluarga & Tanggungan Saya</h3>
                  <span className="text-[10px] font-black bg-red-100 text-asmm-red px-2 py-0.5 rounded-full">
                    {familyMembers.length} Ahli
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium">
                  Pengurusan profil ahli keluarga, semakan status bengkung, dan pemantauan yuran latihan.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-center shrink-0 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  if (isPendingPayment) {
                    showToast('warning', 'Akses Terhad', 'Sila selesaikan dan tunggu pengesahan bayaran pendaftaran terlebih dahulu.');
                    setActiveTab('payments');
                    return;
                  }
                  setActiveTab('keluarga');
                  window.history.pushState(null, '', '/keluarga');
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-extrabold rounded-xl transition-all shadow-xs cursor-pointer active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Daftar Ahli</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isPendingPayment) {
                    showToast('warning', 'Akses Terhad', 'Sila selesaikan dan tunggu pengesahan bayaran pendaftaran terlebih dahulu.');
                    setActiveTab('payments');
                    return;
                  }
                  setActiveTab('keluarga');
                  window.history.pushState(null, '', '/keluarga');
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-red-50 hover:bg-red-100 text-asmm-red text-xs font-extrabold rounded-xl border border-red-100 transition-all cursor-pointer active:scale-95"
              >
                <span>Urus Penuh →</span>
              </button>
            </div>
          </div>

          {familyMembers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {familyMembers.map((child) => {
                const childRank = getSilatRankFromBengkung(child.bengkung || 'Putih 1');
                return (
                  <div
                    key={child.id}
                    className="p-4 bg-slate-50/70 hover:bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 transition-all shadow-2xs hover:shadow-sm flex flex-col justify-between gap-3 group"
                  >
                    <div>
                      {/* Top Row: Photo, Name, Relationship */}
                      <div className="flex items-start gap-3 mb-2.5">
                        <img
                          src={child.photoUrl || '/default-avatar.svg'}
                          alt={child.name}
                          className="w-12 h-12 rounded-xl object-cover border border-slate-200 shadow-2xs shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <span className="text-[9px] font-black uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                              {child.relationship || 'Anak'}
                            </span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${child.status === 'Aktif'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                              }`}>
                              {child.status}
                            </span>
                          </div>
                          <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 truncate group-hover:text-asmm-red transition-colors">
                            {child.name}
                          </h4>
                          <div className="text-[10px] text-slate-500 font-mono truncate">
                            IC: {child.icNo}
                          </div>
                        </div>
                      </div>

                      {/* Bengkung Rank */}
                      <div className="space-y-1 my-2">
                        <div className="w-full">
                          <BengkungCulaBadge bengkung={child.bengkung || 'Putih 1'} size="sm" fullWidth={true} />
                        </div>
                        <div className="text-[10px] font-bold text-asmm-red text-center">
                          {childRank}
                        </div>
                      </div>

                      {/* Gelanggang & Session info */}
                      <div className="text-[11px] text-slate-600 bg-white/80 p-2 rounded-xl border border-slate-200/60 space-y-0.5">
                        <div className="flex items-center gap-1.5 truncate">
                          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="font-bold text-slate-800 truncate">{child.gelanggang}</span>
                        </div>
                        {child.sessionTime && (
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 truncate">
                            <Clock className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="truncate">{child.sessionTime}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/50">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedChildId(child.id);
                          setActiveTab('syllabus');
                        }}
                        className="w-full py-1.5 px-2 bg-red-50 hover:bg-red-100 text-asmm-red font-extrabold text-[11px] rounded-lg transition-all text-center flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <BookOpen className="w-3 h-3" />
                        <span>Silibus</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('keluarga');
                          window.history.pushState(null, '', `/keluarga/edit/${child.id}`);
                        }}
                        className="w-full py-1.5 px-2 bg-white hover:bg-slate-100 text-slate-700 font-extrabold text-[11px] rounded-lg border border-slate-200 transition-all text-center flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <Edit2 className="w-3 h-3" />
                        <span>Kemaskini</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 bg-slate-50/80 rounded-2xl border border-dashed border-slate-200 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 shadow-2xs flex items-center justify-center mx-auto text-asmm-red">
                <Users className="w-6 h-6" />
              </div>
              <div className="max-w-md mx-auto space-y-1">
                <h4 className="text-sm font-extrabold text-slate-900">Belum Ada Profil Ahli Keluarga Didaftarkan</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Sebagai Ketua Gelanggang / Pengurusan, anda boleh mendaftar atau menghubungkan anak & tanggungan untuk memantau kehadiran gelanggang, peperiksaan bengkung, dan yuran silat mereka secara berpusat.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('keluarga');
                  window.history.pushState(null, '', '/keluarga');
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm cursor-pointer active:scale-95"
              >
                <Plus className="w-4 h-4" />
                <span>Daftar Ahli Keluarga Sekarang</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tempahan Saya (Merchandise Orders) for Logged-In User */}
      {currentUser && (
        <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-50 text-asmm-red rounded-2xl border border-red-100/80 shadow-2xs shrink-0">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-extrabold text-slate-900">Tempahan Saya</h3>
                  {myMerchandiseOrders.length > 0 && (
                    <span className="text-[10px] font-black bg-red-100 text-asmm-red px-2 py-0.5 rounded-full">
                      {myMerchandiseOrders.length} Tempahan
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 font-medium">
                  Semak status penyediaan barangan merchandise dan rekod pembelian persatuan anda.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-center shrink-0 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('merchandise');
                  window.history.pushState(null, '', '/merchandise');
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-extrabold rounded-xl transition-all cursor-pointer active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Kedai Barangan</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('pembelian');
                  window.history.pushState(null, '', '/pembelian');
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-xs cursor-pointer active:scale-95"
              >
                <span>Pembelian Saya →</span>
              </button>
            </div>
          </div>

          {myMerchandiseOrders.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {myMerchandiseOrders.slice(0, 3).map((order) => (
                <div
                  key={order.id}
                  onClick={() => {
                    setActiveTab('pembelian');
                    window.history.pushState(null, '', '/pembelian');
                  }}
                  className="p-4 bg-slate-50/70 hover:bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 transition-all shadow-2xs hover:shadow-sm cursor-pointer space-y-2 group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] font-bold text-slate-500">#{order.id.slice(-6)}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                      order.status === 'Selesai'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                        : order.status === 'Sedang Disediakan'
                        ? 'bg-blue-100 text-blue-800 border-blue-200'
                        : order.status === 'Dibatalkan'
                        ? 'bg-slate-100 text-slate-600 border-slate-200'
                        : 'bg-amber-100 text-amber-800 border-amber-200'
                    }`}>
                      <Clock className="w-2.5 h-2.5" />
                      <span>{order.status}</span>
                    </span>
                  </div>

                  <div>
                    <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-asmm-red transition-colors line-clamp-1">
                      {order.itemName}
                    </h4>
                    <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                      <span>Kuantiti: <strong className="text-slate-700">{order.quantity}</strong></span>
                      {order.size && <span>• Saiz: <strong className="text-slate-700">{order.size}</strong></span>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-200/60 text-xs">
                    <span className="font-extrabold text-asmm-red">RM {order.totalAmount.toFixed(2)}</span>
                    <span className="text-[10px] font-bold text-slate-500 group-hover:text-asmm-red transition-colors flex items-center gap-1">
                      <span>Semak</span>
                      <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 bg-slate-50/70 rounded-2xl border border-dashed border-slate-200 text-center space-y-2">
              <ShoppingBag className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-xs text-slate-500 font-medium">
                Tiada rekod tempahan aktif. Kunjungi Kedai ASMM untuk menempah pakaian silat rasmi dan kelengkapan persatuan.
              </p>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('merchandise');
                  window.history.pushState(null, '', '/merchandise');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all shadow-2xs cursor-pointer active:scale-95"
              >
                <span>Buka Kedai Barangan →</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Gelanggang Sesi */}
      <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-extrabold text-slate-900">Senarai Gelanggang Latihan</h3>
            <p className="text-xs text-slate-500">Pilih gelanggang untuk maklumat sesi latihan dan lokasi GPS Google Maps.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setActiveTab('gelanggang');
                window.history.pushState(null, '', '/gelanggang');
              }}
              className="text-xs font-extrabold text-asmm-red hover:underline cursor-pointer"
            >
              Lihat Semua →
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {gelanggangs
            .filter((g) => g.status !== 'Tidak Aktif' && g.status?.toLowerCase() !== 'tidak aktif')
            .map((g) => {
              const isSelected = selectedGelanggang === g.name;
              const mapsUrl = getGoogleMapsUrl(g);

              return (
                <div
                  key={g.id}
                  className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isSelected
                    ? 'bg-red-50/50 border-asmm-red/40 shadow-xs'
                    : 'bg-slate-50/50 border-slate-200/80 hover:bg-slate-50 hover:border-slate-300'
                    }`}
                >
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="p-1 bg-red-50 text-asmm-red rounded-md">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('gelanggang');
                          window.history.pushState(null, '', `/gelanggang?view=calendar&id=${g.id}`);
                        }}
                        className="text-left font-extrabold text-xs text-slate-900 hover:text-asmm-red transition-colors flex items-center gap-1 cursor-pointer border-none bg-transparent p-0"
                        title="Klik untuk maklumat sesi latihan dan kalendar gelanggang"
                      >
                        <span>{g.name}</span>
                        <Calendar className="w-3.5 h-3.5 text-asmm-red opacity-80 shrink-0" />
                      </button>
                      <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">
                        {g.status}
                      </span>
                    </div>

                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-slate-700 hover:text-asmm-red font-semibold leading-relaxed group/addr pl-6"
                      title="Klik untuk membuka lokasi di Google Maps"
                    >
                      <span className="group-hover/addr:underline">{getGelanggangDisplayAddress(g)}</span>
                    </a>

                    <div className="text-[11px] text-slate-500 font-medium pl-6 flex flex-wrap items-center gap-x-4 gap-y-0.5">
                      <div>Hari: <strong className="text-slate-800">{g.sessionTime}</strong></div>
                      <div>Ketua: <strong className="text-asmm-red">{formatPublicName(g.leaderName)}</strong></div>
                    </div>
                  </div>

                  {/* Navigation Action Button */}
                  <div className="shrink-0 sm:self-center pl-6 sm:pl-0 flex flex-col gap-1.5 w-full sm:w-auto">
                    {canEditGelanggangSessionAndPrice(currentUser, g) && (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('gelanggang');
                          window.history.pushState(null, '', `/gelanggang?view=session&id=${g.id}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer border-none"
                      >
                        <Settings className="w-3.5 h-3.5 text-white" />
                        <span>Kemaskini</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('gelanggang');
                        window.history.pushState(null, '', `/gelanggang?view=calendar&id=${g.id}`);
                      }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-xs font-extrabold text-slate-700 hover:text-asmm-red bg-white hover:bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl transition-all cursor-pointer shadow-3xs"
                    >
                      <Calendar className="w-3.5 h-3.5 text-asmm-red shrink-0" />
                      <span>Jadual Sesi</span>
                    </button>
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-xs font-extrabold text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-xl transition-all group/nav"
                    >
                      <Navigation className="w-3.5 h-3.5 text-blue-600 group-hover/nav:scale-110 transition-transform shrink-0" />
                      <span>Google Maps</span>
                    </a>
                  </div>
                </div>
              );
            })}
        </div>
      </div>


      <GelanggangManagementModal
        isOpen={isGelanggangModalOpen}
        onClose={() => {
          setIsGelanggangModalOpen(false);
          setSelectedGelanggangToEdit(null);
        }}
        onSaved={refreshGelanggangs}
        initialGelanggangToEdit={selectedGelanggangToEdit}
      />

      {/* Modal Selesaikan Pendaftaran & Bayar Yuran Ujian Bengkung */}
      {isFinishRegModalOpen && selectedCandidateToPay && activeUjianEvent && (
        <Modal
          isOpen={isFinishRegModalOpen}
          onClose={() => setIsFinishRegModalOpen(false)}
          title="Selesaikan Pendaftaran & Bayar Yuran Ujian Bengkung"
        >
          <div className="space-y-4 text-xs">
            <div className="p-3.5 bg-red-50 border border-red-200/80 rounded-2xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-wider bg-asmm-red text-white px-2 py-0.5 rounded-full">
                  Jadual Acara Rasmi
                </span>
                <span className="font-mono text-xs font-bold text-red-950">
                  {activeUjianEvent.eventDate} ({activeUjianEvent.eventTime || '9.00 Malam'})
                </span>
              </div>
              <h4 className="text-sm font-extrabold text-slate-900">{activeUjianEvent.title}</h4>
              <div className="flex items-center gap-1.5 text-slate-600 font-medium">
                <MapPin className="w-3.5 h-3.5 text-asmm-red shrink-0" />
                <span>{activeUjianEvent.eventLocation || 'Gelanggang Utama ASMM'}</span>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Nama Calon Pesilat:</span>
                <span className="text-slate-900 font-extrabold flex items-center gap-1.5">
                  <span>{selectedCandidateToPay.memberName}</span>
                  <span className="font-mono text-[10px] font-bold text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                    {sanitizeMemberId(selectedCandidateToPay.memberId)}
                  </span>
                </span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Gelanggang Latihan:</span>
                <span className="text-slate-900 font-extrabold">{selectedCandidateToPay.gelanggang}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Kenaikan Pangkat Bengkung:</span>
                <span className="text-asmm-red font-black">
                  {(() => {
                    const m = members.find(mem => mem.id === selectedCandidateToPay.memberId) || (currentUser?.id === selectedCandidateToPay.memberId ? currentUser : null);
                    const cur = cleanBengkungTitle(m?.bengkung || selectedCandidateToPay.currentBengkung);
                    const tgt = cleanBengkungTitle(m?.bengkung ? getNextBengkungRank(m.bengkung) : selectedCandidateToPay.targetBengkung);
                    return `${cur} → ${tgt}`;
                  })()}
                </span>
              </div>
              <div className="flex justify-between items-center pt-1.5">
                <span className="text-slate-700 font-extrabold text-sm">Jumlah Yuran Peperiksaan:</span>
                <span className="text-emerald-700 font-extrabold text-base font-mono">
                  RM {(() => {
                    const m = members.find(mem => mem.id === selectedCandidateToPay.memberId) || (currentUser?.id === selectedCandidateToPay.memberId ? currentUser : null);
                    const tgt = cleanBengkungTitle(m?.bengkung ? getNextBengkungRank(m.bengkung) : selectedCandidateToPay.targetBengkung);
                    return getUjianBengkungFeeForLevel(tgt, ujianFees || undefined).toFixed(2);
                  })()}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsFinishRegModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => handleCreateUjianInvoiceAndPay(selectedCandidateToPay)}
                disabled={isProcessingPayment}
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span>{isProcessingPayment ? 'Memproses...' : 'Sahkan Pendaftaran & Buka Bayaran'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
